import React, { ChangeEvent, useContext, useEffect, useRef } from 'react'

import { Card, Icon, IconButton } from '@mui/material'
import Grid from '@mui/material/Grid'
import Map, { MapRef, Marker } from 'react-map-gl/mapbox'

import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import FormField from 'modules/FormField'
import LoadingComponent from 'modules/Loading'

import { SpecialLocationsForm } from '../../types'
import { SpecialLocationsFormContext } from '../constants'
import 'mapbox-gl/dist/mapbox-gl.css'

function MainForm() {
	const mapRef = useRef<MapRef>(null)
	const isMapFirstRenderRef = useRef(true)
	const { specialLocationsForm, setSpecialLocationsForm, setFormEdited } = useContext(
		SpecialLocationsFormContext
	)
	const locationGeomtery = specialLocationsForm.geometry

	useEffect(() => {
		const map = mapRef.current
		const { lat, lng } = locationGeomtery
		if (!map || lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
			return
		}

		const center: [number, number] = [lng, lat]
		if (isMapFirstRenderRef.current) {
			map.jumpTo({ center, zoom: 16 })
		} else {
			map.flyTo({ center, zoom: 16 })
		}
	}, [locationGeomtery.lat, locationGeomtery.lng])

	const handleChange = (value: any, field: keyof SpecialLocationsForm) => {
		setSpecialLocationsForm((prev: SpecialLocationsForm) => ({
			...prev,
			[field]: value,
		}))
		setFormEdited(true)
	}

	const handleGeometryChange = (value: string, key: 'lat' | 'lng') => {
		setSpecialLocationsForm((prev: SpecialLocationsForm) => ({
			...prev,
			geometry: {
				...prev.geometry,
				[key]: Number(value),
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id="manage-special-locations-form">
			<MDBox p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox display="flex" justifyContent="space-between" alignItems="center">
							<MDBox display="flex" flexDirection="row" gap={2} alignItems="center">
								<MDTypography variant="h5" fontWeight="medium">
									Special Location Details
								</MDTypography>
							</MDBox>
						</MDBox>
					</Grid>
					<Grid item xs={12}>
						<MDBox display="flex" flexDirection="column" gap={1}>
							<MDBox
								display="flex"
								flexDirection="row"
								alignItems="center"
								justifyContent={'space-between'}
								mb={2}
							>
								<MDTypography variant="h5" fontWeight="medium">
									Names
								</MDTypography>
								<IconButton
									size="small"
									onClick={async () => {
										// Add a blank name to the names array
										const updatedNames = [...specialLocationsForm.names, '']
										handleChange(updatedNames, 'names')
									}}
								>
									<Icon fontSize="small" color="info">
										add_circle_outlined
									</Icon>
								</IconButton>
							</MDBox>
							{specialLocationsForm.names.map((name, index) => (
								<MDBox
									key={index}
									display="flex"
									flexDirection={'row'}
									alignItems={'center'}
									width={'100%'}
								>
									<MDBox flex={1}>
										<FormField
											type="tel"
											placeholder={
												index === 0
													? 'Enter primary address (sent to dispatch)'
													: 'Enter alternative address names or variations'
											}
											value={name}
											onChange={(e: ChangeEvent<HTMLInputElement>) => {
												const updatedNames = [...specialLocationsForm.names]
												updatedNames[index] = e.target.value
												handleChange(updatedNames, 'names')
											}}
											error={specialLocationsForm.names[index]?.trim().includes('-')}
											helperText={
												specialLocationsForm.names[index]?.trim().includes('-')
													? 'Names cannot contain hyphens'
													: ''
											}
										/>
									</MDBox>
									{index > 0 && (
										<IconButton
											size="small"
											onClick={async () => {
												// Remove the name at the current index
												const updatedNames = specialLocationsForm.names.filter(
													(_, i) => i !== index
												)
												handleChange(updatedNames, 'names')
											}}
										>
											<Icon fontSize="small" color="error">
												close
											</Icon>
										</IconButton>
									)}
								</MDBox>
							))}
						</MDBox>
					</Grid>
					<Grid item xs={12}>
						<MDBox display="flex" flexDirection="column" gap={1}>
							<MDTypography variant="h5" fontWeight="medium">
								Co-Ordinates
							</MDTypography>
							<MDBox display="flex" flexDirection="row" gap={2}>
								<MDBox flex={1}>
									<FormField
										type="number"
										placeholder="Latitude"
										value={specialLocationsForm.geometry.lat || ''}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleGeometryChange(e.target.value, 'lat')
										}
									/>
								</MDBox>
								<MDBox flex={1}>
									<FormField
										type="number"
										placeholder="Longitude"
										value={specialLocationsForm.geometry.lng || ''}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleGeometryChange(e.target.value, 'lng')
										}
									/>
								</MDBox>
							</MDBox>
						</MDBox>
					</Grid>
					<Grid item xs={12}>
						{locationGeomtery.lng !== undefined && locationGeomtery.lat !== undefined ? (
							<Map
								ref={mapRef}
								mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
								mapStyle="mapbox://styles/mapbox/streets-v11"
								initialViewState={{
									longitude: locationGeomtery.lng,
									latitude: locationGeomtery.lat,
									zoom: 16,
								}}
								style={{
									height: '400px',
									width: '100%',
									borderRadius: '20px',
								}}
								onLoad={() => {
									isMapFirstRenderRef.current = false
								}}
								onClick={(e) => {
									const { lng, lat } = e.lngLat

									setSpecialLocationsForm((prev: SpecialLocationsForm) => ({
										...prev,
										geometry: {
											lat: Number(lat.toFixed(6)),
											lng: Number(lng.toFixed(6)),
										},
									}))
									if (
										specialLocationsForm.names.length >= 1 &&
										specialLocationsForm.names[0].trim() !== ''
									) {
										setFormEdited(true)
									}
								}}
							>
								<Marker
									longitude={locationGeomtery.lng}
									latitude={locationGeomtery.lat}
									anchor="bottom"
								>
									<Icon color="error" fontSize="large">
										location_on
									</Icon>
								</Marker>
							</Map>
						) : (
							<LoadingComponent type="box" />
						)}
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default MainForm
